//core
import React, {useState, useEffect, ChangeEvent, useCallback} from "react";
import { useTranslation } from "react-i18next";
import { FieldHookConfig, useField, useFormikContext } from "formik";
import { Scrollbars } from "react-custom-scrollbars";
import {
  Autocomplete,
  Box,
  Chip,
  TextField,
  Checkbox,
  AutocompleteCloseReason, Tooltip, IconButton,
} from "@mui/material";

//icons
import { CheckBoxOutlineBlank, CheckBox } from "@mui/icons-material";

//styles
import { useStyles } from "./styles";

//helpers
import { importAll } from "../../../../helpers/importAll";

//context
import { AutocompleteSelectContext } from "./context";

//components
import CircularProgress from "@mui/material/CircularProgress";
import CustomPaper from "./CustomPaper";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MAX_HEIGHT = ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP;


interface ICountriesAutocompleteProps  {
  keyValue: string;
  keyId: string;
  value: number | string;
  id: string;
  name: string;
  label: string;
  limitTags?: number;
  disabled?: boolean;
  multiple?: boolean;
  prefix?: string;
  getOptions?: (search: string | null) => any;
  staticOptions?: any;
  showSelectAll?: boolean; // only for staticOptions & multiple
  showSingleSelectAll?: boolean;
  maxHeightFieldScroll?: number; // min value 32, max value 96
  filterOptionsById?: boolean;
  tooltipText?: string,
  disableCloseOnSelect?: boolean,
  isRequired?: boolean;
  onChangeCallback?: () => void;
  placeholder?: string;
}

const MuiStaticAutocomplete = (props: ICountriesAutocompleteProps & FieldHookConfig<string>) => {
  const { setFieldValue, setFieldTouched, validateField } = useFormikContext();
  const { t } = useTranslation();
  const styles = useStyles();

  const [field, meta] = useField(props);

  const {
    keyValue,
    keyId,
    id,
    value,
    name,
    label,
    placeholder,
    disabled = false,
    multiple = false,
    limitTags = -1,
    prefix = '',
    staticOptions = null,
    showSelectAll = false,
    showSingleSelectAll = false,
    maxHeightFieldScroll = 84,
    filterOptionsById = false,
    getOptions,
    tooltipText,
    disableCloseOnSelect = false,
    isRequired = false,
    onChangeCallback,
  } = props;

  const [open, setOpen] = useState<boolean>(false);
  const [options, setOptions] = useState<readonly any[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [singleSelectAll, setSingleSelectAll] = useState(false);
  const [iconsCount, setIconsCount] = useState(1);
  const loading = open && (!!staticOptions && !!staticOptions.length) && options.length === 0;

  useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    if (!!staticOptions) {
      setOptions([
        showSingleSelectAll && {
          [keyId]: 'all',
          label: 'all',
        },
        ...staticOptions,
      ]);
      setOptions(staticOptions);
    }

    if (typeof getOptions === 'function') {
      (async () => {
        const data = await getOptions(null);

        if (active) {
          setOptions(data.payload.list);
        }
      })();
    }

    return () => {
      active = false;
    };
  }, [loading]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  useEffect(() => {
    if (!!value && value.length !== options.length) {
      setSelectAll(false);
    }

    if (!!value && (value.length === staticOptions.length)) {
      setSelectAll(true);
    }

    if (showSingleSelectAll) {
      if (!!value && value.length) {
        if (!!value.filter((item: any) => item.id === 'all').length) {
          setSingleSelectAll(true);
        }
      }
    }
  }, [value]);

  const handleCheckAllStaticOptions = () => {
    if (!!staticOptions) {
      if (!selectAll) {
        setFieldValue(field.name, staticOptions, true);
        setSelectAll(true);
      } else {
        setFieldValue(field.name, [], true);
        setSelectAll(false);
      }
    }
  };

  const handleSingleSelectAll = () => {
    if (!singleSelectAll) {
      setFieldValue(field.name, [{id: 'all', label: 'all'}], true);
      setSingleSelectAll(true);
    } else {
      setFieldValue(field.name, [], true);
      setSingleSelectAll(false);
    }
  }

  const handleChangeInput = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const search = e.target.value;

    if (typeof getOptions === 'function') {
      (async () => {
        const data = await getOptions(search.length >= 2 ? search : null);
        setOptions(data.payload.list);
      })();
    }
  };

  useEffect(() => {
    if (!!value && !!value.length) {
      setIconsCount(3)
    } else {
      setIconsCount(2)
    }
  }, [value]);

  useEffect(() => {
    if (!!tooltipText) {
      setIconsCount(3)
    }
  }, [tooltipText]);

  const setFieldTouchedFn = useCallback(() => {
    setTimeout(() => setFieldTouched(field.name, true), 20)
  }, [field.name]);

  const handleClear = () => {
    //setSelectedAll(false)
    setFieldValue(field.name, []);
    setFieldTouchedFn();
    
    if (typeof onChangeCallback === 'function') {
      onChangeCallback();
    }
  };

  return (
    <AutocompleteSelectContext.Provider value={{
      showSelectAll: showSelectAll,
      selectAllState: selectAll,
      handleCheckAllStaticOptions: handleCheckAllStaticOptions,
      showSingleSelectAll: showSingleSelectAll,
      singleSelectAllState: singleSelectAll,
      handleSingleSelectAll: handleSingleSelectAll,
    }}>
      <Box sx={{ mb: 3 }}>
        <Autocomplete
          disableCloseOnSelect={disableCloseOnSelect}
          className={styles.field}
          id={id}
          size="small"
          multiple={multiple}
          value={value}
          open={open}
          limitTags={limitTags}
          disabled={disabled}
          noOptionsText={t("common.components.autocomplete.no_matches")}
          onOpen={() => {
            if (!disabled) {
              setOpen(true);
            }
          }}
          onClose={(e: any, reason: AutocompleteCloseReason) => {
            if (reason === "escape") {
              setOpen(false);
            }
            setOpen(false);
          }}
          loading={loading}
          options={options.map((option) => option)}
          isOptionEqualToValue={(option, value) => option[keyId] === value[keyId]}
          getOptionLabel={(option) => {
            if (!!prefix) {
              return !!option[keyValue] ? t(`${prefix}${option[keyValue]}`) : '';
            } else {
              return !!option[keyValue] ? `${option[keyValue]}` : '';
            }
          }}
          getOptionDisabled={(option) => singleSelectAll}
          filterOptions={(options, state) => {
            const inputValue = state.inputValue.toLowerCase().trim();

            const displayOptions = options.filter((option) => {
              const label = !!prefix
                ? !!option[keyValue] ? t(`${prefix}${option[keyValue]}`) : ''
                : !!option[keyValue] ? `${option[keyValue]}` : '';

              if (filterOptionsById) {
                return label.toLowerCase().trim()
                    .includes(inputValue) ||
                  option[keyId].toLowerCase().trim()
                    .includes(inputValue)
              } else {
                return label
                  .toLowerCase()
                  .trim()
                  .includes(inputValue);
              }
            });

            return displayOptions;
          }}
          renderOption={(props, option, { selected }) => {
            if (multiple) {
              return (
                <Box
                  sx={{ minHeight: 'auto!important' }}
                  component="li"
                  {...props}
                  key={`${name}-li-${option[keyId]}`}
                  //disabled={singleSelectAll}
                >
                  <Checkbox
                    sx={{ padding: '0', width: 24, height: 24, marginRight: 1 }}
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                    disabled={singleSelectAll}
                  />
                  <Box component='span' sx={{
                    display: 'block',
                    width: '100%',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  }}>
                    {prefix ? t(`${prefix}${option[keyValue]}`) : option[keyValue]}
                  </Box>
                </Box>
              )
            } else {
              return (
                <Box sx={{ minHeight: 'auto!important' }} component="li" {...props} key={`${name}-li-${option[keyId]}`}>
                  <Box component='span' sx={{
                    display: 'block',
                    width: '100%',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  }}>
                    {prefix ? t(`${prefix}${option[keyValue]}`) : option[keyValue]}
                  </Box>
                </Box>
              )
            }
          }}
          renderTags={(tagValue, getTagProps) => {
            const tagArray = showSingleSelectAll
              ? tagValue
              : tagValue.filter(item => item.id !== 'all');

            return tagArray.map((option, index) => (
              <Chip
                size="small"
                variant="filled"
                label={prefix ? t(`${prefix}${option[keyValue]}`) : option[keyValue]}
                {...getTagProps({ index })}
                sx={{ maxWidth: '200px!important' }}
              />
            ))
          }}
          onChange={(e, val, reason) => {
            if (reason === 'clear') {
              setSelectAll(false);
            }

            if (Array.isArray(val) && !val.length) {
              setSingleSelectAll(false);
            }

            setFieldValue(field.name, val, true);

            if (typeof onChangeCallback === 'function') {
              onChangeCallback();
            }

            setTimeout(() => {
              validateField(field.name);
            }, 50)
          }}
          onBlur={(e) => setFieldTouched(field.name, true, true)}
          PaperComponent={CustomPaper}
          ListboxProps={{
            style: { maxHeight: MAX_HEIGHT, padding: 0 }
          }}
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                name={name}
                label={`${label}${isRequired ? '*' : ''}`}
                error={meta.touched && !!meta.error}
                helperText={meta.touched && meta.error ? meta.error : ''}
                placeholder={placeholder ?? t("common.components.autocomplete.placeholder")}
                disabled={disabled}
                InputProps={{
                  ...params.InputProps,
                  sx: {
                    paddingRight: `${iconsCount*34}px!important`
                  },
                  startAdornment: value !== null && !!value.length ? (
                    <Box
                      sx={{
                        paddingRight: multiple && !!value.length ? '30px' : '0px',
                        boxSizing: 'initial',
                      }}
                      onClick={() => {
                        if (!disabled) {
                          setOpen(true);
                        }
                      }}
                    >
                      <Scrollbars
                        className={`${styles.customScroll} ${iconsCount === 3 ? 'more-icons-3' : ''}`}
                        autoHide={true}
                        hideTracksWhenNotNeeded={true}
                        autoHeight={true}
                        autoHeightMin={28}
                        autoHeightMax={maxHeightFieldScroll}
                        renderView={props => <div {...props} className="view"/>}
                        renderTrackHorizontal={props => <div {...props} style={{display: 'none'}} className="track-horizontal"/>}
                        renderTrackVertical={props => <div {...props} className="track-vertical"/>}
                      >
                        {params.InputProps.startAdornment}
                      </Scrollbars>
                    </Box>
                  ) : undefined,
                  endAdornment: (
                    <React.Fragment>
                      <Box className={styles.actions}>
                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                        {!!value && (
                          <IconButton sx={{ padding: '3px' }} aria-label="clear" size="small" onClick={handleClear}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton sx={{ padding: '3px' }} aria-label="clear" size="small" onClick={() => {if (!disabled) {setOpen((prevState) => !prevState)}}}>
                          <ArrowDropDownIcon className={`${styles.arrow} ${open ? styles.arrow_opened : ''}`} />
                        </IconButton>
                        { tooltipText &&
                          <Tooltip title={tooltipText} arrow>
                            <IconButton sx={{ padding: '3px' }} aria-label="clear" size="small" onClick={() => {if (!disabled) {setOpen(true);}}}>
                                <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        }
                        {/*{params.InputProps.endAdornment}*/}
                      </Box>
                    </React.Fragment>
                  ),
                }}
                onChange={(e) => handleChangeInput(e)}
              />
            )
          }}
        />
      </Box>
    </AutocompleteSelectContext.Provider>
  );
};

export default MuiStaticAutocomplete;
